import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAccount } from "wagmi";
import { API_URL } from "../config/contracts";

interface CommissionRecord {
  id: number;
  amount: string;
  tier: number;
  createdAt: string;
}

interface CommissionSummary {
  agentTier: number;
  shadowId: number;
  totalEarned: string;
  pendingOnChain: string;
  recentCommissions: CommissionRecord[];
}

interface TeamMember {
  shadowId: number;
  wallet: string;
  tier: number;
  tierName: string;
  betsCount: number;
  referralsCount: number;
  joinedAt: string;
}

interface TeamInfo {
  agent: { shadowId: number; tier: number; tierName: string };
  directReferrals: TeamMember[];
  totalMembers: number;
}

const TIER_COLORS: Record<number, string> = {
  0: "text-gray-400",
  1: "text-blue-400",
  2: "text-blue-300",
  3: "text-green-400",
  4: "text-green-300",
  5: "text-yellow-400",
  6: "text-orange-400",
  7: "text-red-400",
  8: "text-purple-400",
};

const TIER_RATES: Record<number, string> = {
  1: "5%", 2: "8%", 3: "10%", 4: "12%",
  5: "14%", 6: "16%", 7: "18%", 8: "20%",
};

export function AgentDashboardPage() {
  const { address, isConnected } = useAccount();
  const [summary, setSummary] = useState<CommissionSummary | null>(null);
  const [team, setTeam] = useState<TeamInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<"commission" | "team">("commission");

  useEffect(() => {
    if (isConnected && address) {
      loadData();
    }
  }, [isConnected, address]);

  async function loadData() {
    setLoading(true);
    try {
      const [summaryRes, teamRes] = await Promise.all([
        fetch(`${API_URL}/commission/summary?address=${address}`),
        fetch(`${API_URL}/commission/team?address=${address}`),
      ]);
      if (summaryRes.ok) setSummary(await summaryRes.json());
      if (teamRes.ok) setTeam(await teamRes.json());
    } catch {
      // API not available
    } finally {
      setLoading(false);
    }
  }

  if (!isConnected) {
    return (
      <div className="p-8 max-w-4xl mx-auto">
        <h2 className="text-2xl font-bold mb-6">Agent Dashboard</h2>
        <div className="text-center py-12 bg-gray-900 rounded-lg">
          <p className="text-gray-400 text-lg">Connect your wallet to view dashboard</p>
          <Link to="/" className="text-blue-400 hover:text-blue-300 mt-2 inline-block">
            Go to Home
          </Link>
        </div>
      </div>
    );
  }

  const tierName = summary?.agentTier ? `V${summary.agentTier}` : "Member";

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">Agent Dashboard</h2>
        <Link to="/matches" className="text-blue-400 hover:text-blue-300 text-sm">
          Browse Matches
        </Link>
      </div>

      {/* Agent info card */}
      <div className="bg-gray-900 rounded-lg border border-gray-800 p-6 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">Agent Tier</p>
            <p className={`text-3xl font-bold ${TIER_COLORS[summary?.agentTier || 0] || ""}`}>
              {tierName}
            </p>
            {summary?.agentTier ? (
              <p className="text-xs text-gray-500 mt-1">
                Commission Rate: {TIER_RATES[summary.agentTier] || "0%"}
              </p>
            ) : null}
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-500">Shadow ID</p>
            <p className="text-xl font-mono">#{summary?.shadowId || "\u2014"}</p>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 mt-6">
          <div className="bg-gray-800/50 rounded-lg p-3 text-center">
            <p className="text-xs text-gray-500">Total Earned</p>
            <p className="text-lg font-mono text-green-400">
              {summary?.totalEarned || "0.00"} USDT
            </p>
          </div>
          <div className="bg-gray-800/50 rounded-lg p-3 text-center">
            <p className="text-xs text-gray-500">Pending (On-chain)</p>
            <p className="text-lg font-mono text-yellow-400">
              {summary?.pendingOnChain || "0"} USDT
            </p>
          </div>
          <div className="bg-gray-800/50 rounded-lg p-3 text-center">
            <p className="text-xs text-gray-500">Team Members</p>
            <p className="text-lg font-mono">{team?.totalMembers || 0}</p>
          </div>
        </div>

        {summary?.pendingOnChain && parseFloat(summary.pendingOnChain) > 0 && (
          <button className="mt-4 w-full py-3 bg-green-600 rounded-lg font-semibold hover:bg-green-700 transition">
            Claim Commission
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setActiveTab("commission")}
          className={`flex-1 py-2 rounded-lg text-sm font-medium transition ${
            activeTab === "commission"
              ? "bg-blue-600 text-white"
              : "bg-gray-800 text-gray-400 hover:bg-gray-700"
          }`}
        >
          Commission History
        </button>
        <button
          onClick={() => setActiveTab("team")}
          className={`flex-1 py-2 rounded-lg text-sm font-medium transition ${
            activeTab === "team"
              ? "bg-blue-600 text-white"
              : "bg-gray-800 text-gray-400 hover:bg-gray-700"
          }`}
        >
          Team Tree
        </button>
      </div>

      {activeTab === "commission" ? (
        <div className="space-y-2">
          {loading ? (
            <div className="animate-pulse space-y-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-12 bg-gray-800 rounded-lg" />
              ))}
            </div>
          ) : (summary?.recentCommissions?.length || 0) === 0 ? (
            <div className="text-center py-8 bg-gray-900 rounded-lg">
              <p className="text-gray-400">No commissions yet</p>
            </div>
          ) : (
            summary?.recentCommissions.map((c) => (
              <div
                key={c.id}
                className="bg-gray-900 rounded-lg border border-gray-800 p-3 flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <span className={`text-sm font-semibold ${TIER_COLORS[c.tier] || ""}`}>
                    V{c.tier}
                  </span>
                  <span className="text-xs text-gray-500">
                    {new Date(c.createdAt).toLocaleDateString()}
                  </span>
                </div>
                <span className="font-mono text-green-400">
                  +{parseFloat(c.amount).toFixed(4)} USDT
                </span>
              </div>
            ))
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {loading ? (
            <div className="animate-pulse space-y-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-16 bg-gray-800 rounded-lg" />
              ))}
            </div>
          ) : (team?.directReferrals?.length || 0) === 0 ? (
            <div className="text-center py-8 bg-gray-900 rounded-lg">
              <p className="text-gray-400">No team members yet</p>
              <p className="text-gray-500 text-sm mt-1">
                Share your referral link to build your team
              </p>
            </div>
          ) : (
            team?.directReferrals.map((m) => (
              <div
                key={m.shadowId}
                className="bg-gray-900 rounded-lg border border-gray-800 p-4"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm">#{m.shadowId}</span>
                      <span className={`text-xs font-semibold ${TIER_COLORS[m.tier] || ""}`}>
                        {m.tierName}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1 font-mono">
                      {m.wallet.slice(0, 6)}...{m.wallet.slice(-4)}
                    </p>
                  </div>
                  <div className="text-right text-xs text-gray-400">
                    <p>{m.betsCount} bets</p>
                    <p>{m.referralsCount} referrals</p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
